import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { User, Lock, Trash2, ArrowLeft, CheckCircle2, XCircle, Mail, Loader2, Bell } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Link } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

const AccountPage = () => {
  const { user, profile, updateProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [displayName, setDisplayName] = useState(profile?.display_name || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [subscribeNewsletter, setSubscribeNewsletter] = useState(profile?.subscribe_newsletter ?? false);
  const [subscribePriceDrops, setSubscribePriceDrops] = useState(profile?.subscribe_price_drops ?? false);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const [isUpdatingSubscriptions, setIsUpdatingSubscriptions] = useState(false);

  const isEmailVerified = !!user?.email_confirmed_at;

  const handleResendVerification = async () => {
    if (!user?.email) return;
    
    setIsResendingVerification(true);
    
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: user.email,
    });
    
    if (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to send verification email.",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Verification Email Sent",
        description: "Please check your inbox for the verification link.",
      });
    }
    
    setIsResendingVerification(false);
  };

  if (!user) {
    navigate('/auth');
    return null;
  }

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUpdatingProfile(true);
    
    const { error } = await updateProfile({ display_name: displayName });
    
    if (error) {
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Profile Updated",
        description: "Your display name has been updated successfully.",
      });
    }
    
    setIsUpdatingProfile(false);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast({
        title: "Error",
        description: "New passwords do not match.",
        variant: "destructive",
      });
      return;
    }
    
    if (newPassword.length < 6) {
      toast({
        title: "Error",
        description: "Password must be at least 6 characters.",
        variant: "destructive",
      });
      return;
    }
    
    setIsChangingPassword(true);
    
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    
    if (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to change password.",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Password Changed",
        description: "Your password has been updated successfully.",
      });
      setNewPassword('');
      setConfirmPassword('');
    }
    
    setIsChangingPassword(false);
  };

  const handleUpdateSubscriptions = async (newsletter: boolean, priceDrops: boolean) => {
    setIsUpdatingSubscriptions(true);
    
    const { error } = await updateProfile({
      subscribe_newsletter: newsletter,
      subscribe_price_drops: priceDrops,
    });
    
    if (error) {
      toast({
        title: "Error",
        description: "Failed to update subscription preferences.",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Preferences Updated",
        description: "Your subscription preferences have been saved.",
      });
    }
    
    setIsUpdatingSubscriptions(false);
  };

  const handleDeleteAccount = async () => {
    setIsDeletingAccount(true);
    
    try {
      // Use atomic server-side function to delete all user data
      const { error } = await supabase.rpc('delete_user_account');
      
      if (error) {
        console.error('Account deletion error:', error);
        toast({
          title: "Error",
          description: "Failed to delete account. Please try again.",
          variant: "destructive",
        });
        setIsDeletingAccount(false);
        return;
      }
      
      // Sign out the user after successful deletion
      await signOut();
      
      toast({
        title: "Account Cancelled",
        description: "Your account data has been removed. You have been signed out.",
      });
      
      navigate('/');
    } catch (error) {
      console.error('Account deletion error:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
      setIsDeletingAccount(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Account Settings | GPU Benchmark Hub</title>
        <meta name="description" content="Manage your account settings, update your profile, change password, or cancel your account." />
      </Helmet>
      
      <div className="min-h-screen bg-background py-8 px-4">
        <div className="container max-w-2xl mx-auto space-y-6">
          <Link to="/gpu-comparison" className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to GPU Comparison
          </Link>
          
          <h1 className="text-3xl font-bold text-foreground">Account Settings</h1>
          
          {/* Profile Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Profile Information
              </CardTitle>
              <CardDescription>Update your display name and profile details.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="flex items-center gap-2">
                    <Input id="email" type="email" value={user.email || ''} disabled className="bg-muted flex-1" />
                    {isEmailVerified ? (
                      <Badge variant="outline" className="border-green-500 text-green-600 gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        Verified
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="border-amber-500 text-amber-600 gap-1">
                        <XCircle className="h-3 w-3" />
                        Unverified
                      </Badge>
                    )}
                  </div>
                  {!isEmailVerified && (
                    <div className="flex items-center gap-2 mt-2">
                      <p className="text-xs text-muted-foreground">Your email is not verified.</p>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs"
                        onClick={handleResendVerification}
                        disabled={isResendingVerification}
                      >
                        {isResendingVerification ? (
                          <>
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Mail className="h-3 w-3 mr-1" />
                            Resend verification email
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                  {isEmailVerified && (
                    <p className="text-xs text-muted-foreground">Email cannot be changed.</p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="displayName">Display Name</Label>
                  <Input
                    id="displayName"
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Enter your display name"
                  />
                </div>
                
                <Button type="submit" disabled={isUpdatingProfile}>
                  {isUpdatingProfile ? 'Saving...' : 'Save Changes'}
                </Button>
              </form>
            </CardContent>
          </Card>
          
          {/* Password Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Change Password
              </CardTitle>
              <CardDescription>Update your password to keep your account secure.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    minLength={6}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    minLength={6}
                  />
                </div>
                
                <Button type="submit" disabled={isChangingPassword || !newPassword || !confirmPassword}>
                  {isChangingPassword ? 'Changing...' : 'Change Password'}
                </Button>
              </form>
            </CardContent>
          </Card>
          
          {/* Subscription Preferences Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Email Preferences
              </CardTitle>
              <CardDescription>Manage your email subscription preferences.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="newsletter" className="text-base">Newsletter</Label>
                  <p className="text-sm text-muted-foreground">
                    Receive GPU news, deals, and updates
                  </p>
                </div>
                <Switch
                  id="newsletter"
                  checked={subscribeNewsletter}
                  disabled={isUpdatingSubscriptions}
                  onCheckedChange={(checked) => {
                    setSubscribeNewsletter(checked);
                    handleUpdateSubscriptions(checked, subscribePriceDrops);
                  }}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="priceDrops" className="text-base">Price Drop Alerts</Label>
                  <p className="text-sm text-muted-foreground">
                    Get notified when GPUs you're watching drop in price
                  </p>
                </div>
                <Switch
                  id="priceDrops"
                  checked={subscribePriceDrops}
                  disabled={isUpdatingSubscriptions}
                  onCheckedChange={(checked) => {
                    setSubscribePriceDrops(checked);
                    handleUpdateSubscriptions(subscribeNewsletter, checked);
                  }}
                />
              </div>
            </CardContent>
          </Card>
          
          {/* Delete Account Section */}
          <Card className="border-destructive/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Cancel Account
              </CardTitle>
              <CardDescription>Permanently delete your account and all associated data.</CardDescription>
            </CardHeader>
            <CardContent>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive">Cancel My Account</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete your account data, 
                      including all your price alerts and profile information.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDeleteAccount}
                      disabled={isDeletingAccount}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {isDeletingAccount ? 'Deleting...' : 'Yes, delete my account'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default AccountPage;
